import { memo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { PicklistDto } from '../types';
import { aggregate, gapCard, qty, type PillTone } from '../utils/picklistProgress';

/**
 * PW-13 — the detail page's KPI strip.
 *
 * ## What these four cards replace, and why the swap is not cosmetic
 *
 * The old strip was *Total Items* (a line count), *Picked* (a line count), *Progress* (line-count
 * `progressPercentage`) and *Material Requests*. Three of the four answered "how many rows?" on a page
 * whose whole subject is QUANTITY — a picklist for 240 pcs with 190 in hand read "0 of 1 items picked, 0%".
 *
 * All four cards here are quantity-weighted and computed from the same module the landing uses, so the two
 * surfaces cannot disagree about the same picklist.
 *
 * ## The MRS deep link moved, it did not disappear
 *
 * Card 4 takes the *Material Requests* card's place, and that card held the ONLY one-click route from a
 * picklist to its MRS. The links moved into the meta line in the page header — in the same commit, on
 * purpose (D36). If a future change removes them from there, this comment is where the trail leads back.
 */

const METER_TONE: Record<PillTone, string> = {
  success: 'bg-success',
  warning: 'bg-warning',
  info: 'bg-info',
  destructive: 'bg-destructive',
  secondary: 'bg-muted-foreground',
  muted: 'bg-muted-foreground',
};

const VALUE_TONE: Record<PillTone, string> = {
  success: 'text-success',
  warning: 'text-warning',
  info: 'text-info',
  destructive: 'text-destructive',
  secondary: 'text-foreground',
  muted: 'text-foreground',
};

interface KpiCardProps {
  label: string;
  value: string;
  /** Sits on the SAME baseline as the value, small and muted — "220  97% of qty". */
  inlineNote?: string;
  subLine?: string;
  tone?: PillTone;
  /** The 6px meter under the value. Percentages, 0–100. Its tooltip explains the segments. */
  meter?: { primaryPct: number; flightPct?: number; tone: PillTone; tooltip: string };
}

function KpiCard({ label, value, inlineNote, subLine, tone, meter }: KpiCardProps) {
  return (
    <Card className="h-full">
      <CardContent className="space-y-2 p-4">
        <p className="text-xs font-medium text-muted-foreground">{label}</p>
        <p className="flex items-baseline gap-2">
          <span className={cn('text-2xl font-bold leading-none tabular-nums', tone && VALUE_TONE[tone])}>
            {value}
          </span>
          {inlineNote && <span className="text-xs text-muted-foreground">{inlineNote}</span>}
        </p>
        {meter && (
          // The tooltip belongs to the BAR, not the card. A focusable card wrapper would put a tab stop
          // in front of every KPI to reach an explanation of one segment inside it.
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                tabIndex={0}
                role="img"
                aria-label={meter.tooltip}
                className="flex h-1.5 w-full overflow-hidden rounded-full bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <div className={cn('h-full', METER_TONE[meter.tone])} style={{ width: `${meter.primaryPct}%` }} />
                {meter.flightPct !== undefined && meter.flightPct > 0 && (
                  <div className="picklist-flight h-full" style={{ width: `${meter.flightPct}%` }} />
                )}
              </div>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">{meter.tooltip}</TooltipContent>
          </Tooltip>
        )}
        {subLine && <p className="text-xs text-muted-foreground">{subLine}</p>}
      </CardContent>
    </Card>
  );
}

export const PicklistKpiStrip = memo(function PicklistKpiStrip({ picklist }: { picklist: PicklistDto }) {
  const agg = aggregate(picklist);
  const gap = gapCard(agg);
  const unit = agg.unit ? ` ${agg.unit}` : '';
  const den = Math.max(agg.toPick, 1);

  const pickedPct = Math.round((agg.picked / den) * 100);
  const postedPct = Math.round((agg.posted / den) * 100);

  return (
    // PT-7 A9 — 2 columns, 4 once the CONTENT COLUMN reaches 1100px (`.pl-cq-kpi`, globals.css). The viewport
    // classes this replaced broke UI-GUIDELINES §6: the column, not the window, is what the cards share.
    <div className="pl-cq-kpi">
      <KpiCard
        label="Quantity to pick"
        value={`${qty(agg.toPick)}${unit}`}
        // Computed here, not from `pickedItemsCount` — that counts IsPicked FLAGS, and a strip whose four
        // cards come from two different definitions of "picked" will disagree with itself on screen.
        subLine={`${agg.itemCount} line item${agg.itemCount === 1 ? '' : 's'} · ${agg.fullyPicked} fully picked`}
      />

      <KpiCard
        label="Picked"
        value={`${qty(agg.picked)}${unit}`}
        inlineNote={`${pickedPct}% of qty`}
        meter={{
          primaryPct: (agg.picked / den) * 100,
          tone: 'info',
          tooltip: `${qty(agg.picked)} of ${qty(agg.toPick)}${unit} is physically picked. Picking does not move NetSuite inventory — posting does.`,
        }}
      />

      <KpiCard
        label="Posted to NetSuite"
        value={`${qty(agg.posted)}${unit}`}
        inlineNote={`${postedPct}% of qty`}
        subLine={
          agg.inflight > 0
            ? `+ ${qty(agg.inflight)} posting to NetSuite now`
            : 'Only posted quantities have moved NetSuite inventory'
        }
        meter={{
          primaryPct: (agg.posted / den) * 100,
          flightPct: (agg.inflight / den) * 100,
          tone: 'success',
          tooltip: agg.inflight > 0
            ? `Solid green: ${qty(agg.posted)}${unit} confirmed by NetSuite. Striped: ${qty(agg.inflight)}${unit} in an open posting batch, not confirmed yet.`
            : `${qty(agg.posted)} of ${qty(agg.toPick)}${unit} has reached NetSuite as MIS.`,
        }}
      />

      <KpiCard
        label={gap.title}
        value={`${qty(gap.value)}${unit}`}
        subLine={gap.subLine}
        tone={gap.tone}
      />
    </div>
  );
});
